// src/components/BanksReport.jsx
// Reporte de Bancos: recepción de pagos vs pagos hechos, gráficas y KPIs

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3,
  Filter,
  RefreshCw,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  CreditCard,
  X,
  Download,
  ArrowUp,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import * as XLSX from 'xlsx';
import apiService from '../services/apiService';
import { errorLog } from '../config/featureFlags';

const COLORS = [
  '#8b5cf6', '#ec4899', '#06b6d4', '#10b981', '#f59e0b',
  '#ef4444', '#6366f1', '#14b8a6', '#f97316', '#84cc16',
];

const formatCOP = (n) => {
  if (n == null || isNaN(n)) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

const formatUSD = (n) => {
  if (n == null || isNaN(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const BanksReport = () => {
  const [trips, setTrips] = useState([]);
  const [envivos, setEnvivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState({
    tripId: '',
    envivoId: '',
    bankFilter: '', // Filtro local por banco/tarjeta
    startDate: '',
    endDate: '',
  });

  const [summary, setSummary] = useState(null);
  const [byEnvivo, setByEnvivo] = useState({ data: [], total: 0 });
  const [byBank, setByBank] = useState({ data: [], filters_applied: {} });
  const [envivoPayments, setEnvivoPayments] = useState({ data: [], total: 0 });
  const [orderPaymentsByEnvivo, setOrderPaymentsByEnvivo] = useState({ data: [], filters_applied: {} });

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeTab, setActiveTab] = useState('pagos-hechos'); // pagos-hechos | recepcion | detalle

  const loadTrips = async () => {
    try {
      const data = await apiService.trip.getAll();
      setTrips(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Error al cargar viajes: ' + err.message);
      setTrips([]);
    }
  };

  const loadEnvivos = useCallback(async (tripId = null) => {
    try {
      const data = tripId
        ? await apiService.envivo.getByTrip(tripId)
        : await apiService.envivo.getAll();
      setEnvivos(Array.isArray(data) ? data : []);
    } catch (err) {
      setEnvivos([]);
    }
  }, []);

  useEffect(() => {
    loadTrips();
  }, []);

  useEffect(() => {
    if (filters.tripId) {
      loadEnvivos(filters.tripId);
      setFilters((prev) => ({ ...prev, envivoId: '' }));
    } else {
      loadEnvivos();
    }
  }, [filters.tripId, loadEnvivos]);

  const buildQuery = useCallback(() => {
    const q = {};
    if (filters.tripId) q.tripId = filters.tripId;
    if (filters.envivoId) q.envivoId = filters.envivoId;
    if (filters.startDate) q.startDate = filters.startDate;
    if (filters.endDate) q.endDate = filters.endDate;
    return q;
  }, [filters.tripId, filters.envivoId, filters.startDate, filters.endDate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    const query = buildQuery();
    try {
      const [summaryRes, byEnvivoRes, byBankRes, envivoPayRes, orderPayRes] = await Promise.all([
        apiService.banksReport.getSummary(query),
        apiService.banksReport.getByEnvivo(query),
        apiService.banksReport.getByBank(query),
        apiService.banksReport.getEnvivoPayments(query),
        apiService.banksReport.getOrderPaymentsByEnvivo(query),
      ]);
      setSummary(summaryRes);
      setByEnvivo({
        data: Array.isArray(byEnvivoRes.data) ? byEnvivoRes.data : [],
        total: byEnvivoRes.total ?? byEnvivoRes.data?.length ?? 0,
      });
      setByBank({
        data: Array.isArray(byBankRes.data) ? byBankRes.data : [],
        filters_applied: byBankRes.filters_applied || {},
      });
      setEnvivoPayments({
        data: Array.isArray(envivoPayRes.data) ? envivoPayRes.data : [],
        total: envivoPayRes.total ?? envivoPayRes.data?.length ?? 0,
      });
      setOrderPaymentsByEnvivo({
        data: Array.isArray(orderPayRes.data) ? orderPayRes.data : [],
        filters_applied: orderPayRes.filters_applied || {},
      });
    } catch (err) {
      errorLog('Error cargando Reporte de Bancos:', err);
      setError(err.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const applyBankFilter = useCallback((items, nameKey) => {
    const bf = (filters.bankFilter || '').trim().toLowerCase();
    if (!bf) return items;
    return items.filter((item) => (item[nameKey] || '').toLowerCase().includes(bf));
  }, [filters.bankFilter]);

  const summaryData = summary?.summary ?? (typeof summary?.total_cop !== 'undefined' ? summary : null);
  const byCardData = useMemo(
    () => (summary && Array.isArray(summary.by_card) ? summary.by_card : []),
    [summary],
  );
  const byEnvivoData = useMemo(
    () => (summary && Array.isArray(summary.by_envivo) ? summary.by_envivo : []),
    [summary],
  );

  const byBankFiltered = useMemo(
    () => applyBankFilter(byBank.data, 'card_name'),
    [byBank.data, applyBankFilter],
  );
  const byEnvivoFiltered = useMemo(
    () => applyBankFilter(byEnvivo.data, 'envivo_name'),
    [byEnvivo.data, applyBankFilter],
  );

  const pieDataByCard = useMemo(
    () =>
      (filters.bankFilter
        ? byBankFiltered.map((r) => ({ name: r.card_name, value: r.total_cop || 0 }))
        : byCardData.map((r) => ({ name: r.card_name, value: r.total_cop || 0 }))).filter(
        (d) => d.value > 0,
      ),
    [filters.bankFilter, byBankFiltered, byCardData],
  );

  const barDataByEnvivo = useMemo(() => {
    const source = filters.bankFilter ? byEnvivoFiltered : byEnvivoData;
    return source.map((r) => ({
      name: (r.envivo_name || r.name || '').substring(0, 20),
      total_cop: r.total_cop || 0,
      total_usd: r.total_usd || 0,
      count: r.payments_count || r.count || 0,
    }));
  }, [filters.bankFilter, byEnvivoFiltered, byEnvivoData]);

  const recepcionPorBanco = useMemo(() => {
    const out = [];
    (orderPaymentsByEnvivo.data || []).forEach((ev) => {
      (ev.by_bank || []).forEach((b) => {
        const exists = out.find((o) => o.bank_name === b.bank_name);
        if (exists) {
          exists.total_amount += b.total_amount || 0;
          exists.payments_count += b.payments_count || 0;
        } else {
          out.push({
            bank_name: b.bank_name,
            total_amount: b.total_amount || 0,
            payments_count: b.payments_count || 0,
          });
        }
      });
    });
    return applyBankFilter(out, 'bank_name');
  }, [orderPaymentsByEnvivo.data, applyBankFilter]);

  const totalRecepcionCOP = useMemo(
    () => (orderPaymentsByEnvivo.data || []).reduce((s, ev) => s + (ev.total_cop || 0), 0),
    [orderPaymentsByEnvivo.data],
  );

  const totalPagosHechosCOP = summaryData?.total_cop ?? 0;
  const totalPagosHechosUSD = summaryData?.total_usd ?? 0;
  const diferenciaConciliacion = totalRecepcionCOP - totalPagosHechosCOP;

  const lineDataByDate = useMemo(() => {
    const map = new Map();
    (byBank.data || []).forEach((b) => {
      (b.by_date || []).forEach((d) => {
        const key = d.date;
        if (!map.has(key)) map.set(key, { date: key, total_cop: 0, total_usd: 0 });
        const row = map.get(key);
        row.total_cop += d.amount_cop || 0;
        row.total_usd += (row.total_usd || 0) + (d.amount_usd || 0);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [byBank.data]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheets = [];

    if (summaryData) {
      sheets.push({
        name: 'Resumen',
        data: [
          ['Total USD (Pagos hechos)', totalPagosHechosUSD],
          ['Total COP (Pagos hechos)', totalPagosHechosCOP],
          ['Total recepción COP', totalRecepcionCOP],
          ['Diferencia conciliación COP', diferenciaConciliacion],
          ['En vivos', summaryData.envivos_count ?? summaryData.total_envivo_payments ?? ''],
        ],
      });
    }

    if (byEnvivo.data.length > 0) {
      const headers = ['En vivo', 'Viaje', 'Total USD', 'Total COP', 'Cant. pagos'];
      const rows = byEnvivo.data.map((r) => [
        r.envivo_name,
        r.trip_name,
        r.total_usd,
        r.total_cop,
        r.payments_count,
      ]);
      sheets.push({ name: 'Por En vivo', data: [headers, ...rows] });
    }

    if (envivoPayments.data.length > 0) {
      const headers = ['En vivo', 'Viaje', 'Tarjeta', 'USD', 'COP', 'TRM', 'Fecha'];
      const rows = envivoPayments.data.map((p) => [
        p.envivo_name,
        p.trip_name,
        p.card_name,
        p.amount_usd,
        p.amount_cop,
        p.trm_rate,
        p.payment_date,
      ]);
      sheets.push({ name: 'Pagos al cierre', data: [headers, ...rows] });
    }

    if (orderPaymentsByEnvivo.data.length > 0) {
      const rows = [];
      orderPaymentsByEnvivo.data.forEach((ev) => {
        (ev.by_bank || []).forEach((b) => {
          rows.push([ev.envivo_name, ev.trip_name, b.bank_name, b.total_amount, b.payments_count]);
        });
      });
      if (rows.length > 0) {
        sheets.push({
          name: 'Recepción por banco',
          data: [['En vivo', 'Viaje', 'Banco', 'Total COP', 'Cant.'], ...rows],
        });
      }
    }

    sheets.forEach((s) => {
      const ws = XLSX.utils.aoa_to_sheet(s.data);
      XLSX.utils.book_append_sheet(wb, ws, s.name.substring(0, 31));
    });

    XLSX.writeFile(wb, `reporte_bancos_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-100 text-emerald-700">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Reporte de Bancos</h1>
              <p className="text-sm text-gray-500">
                Recepción de pagos vs pagos hechos • Gráficas y KPIs
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              <Filter size={18} />
              {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Actualizar
            </button>
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              <Download size={18} />
              Excel
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="p-1 hover:bg-red-100 rounded">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Filtros */}
        {showFilters && (
          <div className="mb-6 p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Filtros</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Viaje</label>
                <select
                  value={filters.tripId}
                  onChange={(e) => setFilters((p) => ({ ...p, tripId: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                >
                  <option value="">Todos</option>
                  {(trips || []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name || `Viaje ${t.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">En vivo</label>
                <select
                  value={filters.envivoId}
                  onChange={(e) => setFilters((p) => ({ ...p, envivoId: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                >
                  <option value="">Todos</option>
                  {(envivos || []).map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name || `En vivo ${e.id}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Banco / tarjeta (buscar)
                </label>
                <input
                  type="text"
                  value={filters.bankFilter}
                  onChange={(e) => setFilters((p) => ({ ...p, bankFilter: e.target.value }))}
                  placeholder="Ej: Bancolombia, Nequi..."
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Fecha inicio</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Fecha fin</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-10 h-10 text-emerald-600 animate-spin" />
          </div>
        )}

        {!loading && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <DollarSign size={16} />
                  Pagos hechos (USD)
                </div>
                <div className="text-lg font-bold text-gray-900">{formatUSD(totalPagosHechosUSD)}</div>
              </div>
              <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <Wallet size={16} />
                  Pagos hechos (COP)
                </div>
                <div className="text-lg font-bold text-gray-900">{formatCOP(totalPagosHechosCOP)}</div>
              </div>
              <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                  <TrendingUp size={16} />
                  Recepción (COP)
                </div>
                <div className="text-lg font-bold text-green-700">{formatCOP(totalRecepcionCOP)}</div>
              </div>
              <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <CreditCard size={16} />
                  En vivos
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {summaryData?.envivos_count ?? byEnvivo.data?.length ?? 0}
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-sm mb-1">
                  {diferenciaConciliacion >= 0 ? (
                    <TrendingUp size={16} className="text-emerald-600" />
                  ) : (
                    <TrendingDown size={16} className="text-amber-600" />
                  )}
                  <span className={diferenciaConciliacion >= 0 ? 'text-emerald-600' : 'text-amber-600'}>
                    Conciliación
                  </span>
                </div>
                <div
                  className={`text-lg font-bold ${
                    diferenciaConciliacion >= 0 ? 'text-emerald-700' : 'text-amber-700'
                  }`}
                >
                  {formatCOP(diferenciaConciliacion)}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Recepción − Pagos hechos</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 border-b border-gray-200">
              {[
                { id: 'pagos-hechos', label: 'Pagos hechos', icon: CreditCard },
                { id: 'recepcion', label: 'Recepción por banco', icon: TrendingUp },
                { id: 'detalle', label: 'Detalle crudo', icon: BarChart3 },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Gráficas y tablas según tab */}
            {activeTab === 'pagos-hechos' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {pieDataByCard.length > 0 && (
                    <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        Pagos hechos por tarjeta (COP)
                      </h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={pieDataByCard}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, value }) =>
                              `${(name || '').substring(0, 15)}... ${formatCOP(value)}`
                            }
                          >
                            {pieDataByCard.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => formatCOP(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {barDataByEnvivo.length > 0 && (
                    <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        Pagos hechos por en vivo (COP)
                      </h3>
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={barDataByEnvivo} margin={{ top: 5, right: 20, left: 0, bottom: 120 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey="name"
                            tick={({ x, y, payload }) => (
                              <g transform={`translate(${x}, ${y + 35})`}>
                                <text
                                  textAnchor="start"
                                  transform="rotate(90 0 0)"
                                  fontSize={11}
                                  fill="#374151"
                                >
                                  {payload.value}
                                </text>
                              </g>
                            )}
                            height={110}
                            interval={0}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
                          <Tooltip formatter={(v) => [formatCOP(v), 'Total COP']} />
                          <Bar dataKey="total_cop" fill="#10b981" name="Total COP" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                {lineDataByDate.length > 0 && (
                  <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">
                      Evolución pagos hechos por fecha (COP)
                    </h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={lineDataByDate}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" />
                        <YAxis tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
                        <Tooltip formatter={(v) => [formatCOP(v), 'Total COP']} />
                        <Line type="monotone" dataKey="total_cop" stroke="#8b5cf6" strokeWidth={2} name="Total COP" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Tabla por en vivo con pagos */}
                {byEnvivo.data.length > 0 && (
                  <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm overflow-x-auto">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Detalle por en vivo</h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2">En vivo</th>
                          <th className="text-left py-2">Viaje</th>
                          <th className="text-right py-2">Total USD</th>
                          <th className="text-right py-2">Total COP</th>
                          <th className="text-right py-2">Pagos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byEnvivo.data.map((r) => (
                          <tr key={r.envivo_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2">{r.envivo_name}</td>
                            <td className="py-2">{r.trip_name}</td>
                            <td className="text-right py-2">{formatUSD(r.total_usd)}</td>
                            <td className="text-right py-2">{formatCOP(r.total_cop)}</td>
                            <td className="text-right py-2">{r.payments_count ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'recepcion' && (
              <div className="space-y-6">
                {recepcionPorBanco.length > 0 && (
                  <>
                    <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        Recepción de pagos por banco (clientes → tú)
                      </h3>
                      <ResponsiveContainer width="100%" height={Math.max(400, recepcionPorBanco.length * 52)}>
                        <BarChart
                          data={recepcionPorBanco.map((b) => ({
                            name: b.bank_name,
                            total: b.total_amount,
                            count: b.payments_count,
                          }))}
                          layout="vertical"
                          margin={{ left: 150, right: 30, top: 5, bottom: 5 }}
                          barSize={36}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" tickFormatter={(v) => formatCOP(v)} />
                          <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 13 }} />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0]?.payload;
                              if (!d) return null;
                              return (
                                <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-sm">
                                  <div className="font-semibold text-gray-800 mb-2">{d.name}</div>
                                  <div className="space-y-1">
                                    <div className="flex justify-between gap-4">
                                      <span className="text-gray-600">Total:</span>
                                      <span className="font-semibold">{formatCOP(d.total)}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-gray-600">Transacciones:</span>
                                      <span className="font-semibold">{d.count?.toLocaleString('es-CO')}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            }}
                          />
                          <Bar dataKey="total" fill="#06b6d4" name="Total COP" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm overflow-x-auto">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        Recepción por banco y en vivo
                      </h3>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2">En vivo</th>
                            <th className="text-left py-2">Viaje</th>
                            <th className="text-left py-2">Banco</th>
                            <th className="text-right py-2">Total COP</th>
                            <th className="text-right py-2">Transacciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderPaymentsByEnvivo.data.map((ev) =>
                            (ev.by_bank || []).map((b, i) => (
                              <tr
                                key={`${ev.envivo_id}-${b.bank_name}-${i}`}
                                className="border-b border-gray-100 hover:bg-gray-50"
                              >
                                <td className="py-2">{ev.envivo_name}</td>
                                <td className="py-2">{ev.trip_name}</td>
                                <td className="py-2">{b.bank_name}</td>
                                <td className="text-right py-2">{formatCOP(b.total_amount)}</td>
                                <td className="text-right py-2">{b.payments_count}</td>
                              </tr>
                            )),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {recepcionPorBanco.length === 0 && (
                  <div className="p-8 text-center text-gray-500 rounded-xl bg-gray-50">
                    No hay datos de recepción de pagos con los filtros aplicados.
                  </div>
                )}
              </div>
            )}

            {activeTab === 'detalle' && (
              <div className="p-4 rounded-xl bg-white border border-gray-100 shadow-sm overflow-x-auto">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  Listado crudo de pagos al cierre (envivo_payments)
                </h3>
                {envivoPayments.data.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2">En vivo</th>
                        <th className="text-left py-2">Viaje</th>
                        <th className="text-left py-2">Tarjeta</th>
                        <th className="text-right py-2">USD</th>
                        <th className="text-right py-2">COP</th>
                        <th className="text-right py-2">TRM</th>
                        <th className="text-left py-2">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {envivoPayments.data.map((p) => (
                        <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2">{p.envivo_name}</td>
                          <td className="py-2">{p.trip_name}</td>
                          <td className="py-2">{p.card_name}</td>
                          <td className="text-right py-2">{formatUSD(p.amount_usd)}</td>
                          <td className="text-right py-2">{formatCOP(p.amount_cop)}</td>
                          <td className="text-right py-2">{p.trm_rate ? p.trm_rate.toFixed(2) : '-'}</td>
                          <td className="py-2">{p.payment_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-8 text-center text-gray-500">No hay pagos al cierre con los filtros aplicados.</div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-2 rounded-full bg-emerald-600 text-white shadow-lg hover:bg-emerald-700 z-40"
        >
          <ArrowUp size={24} />
        </button>
      )}
    </div>
  );
};

export default BanksReport;
